/**
 * An alert's record in the side sheet (v1 completion I §10, the mock-up's
 * states 2–5): what it is about, who owns it and when it is due, why it fired
 * (the detector's sentence), its ticket, then its own commands (Acknowledge,
 * Mark done…) and its subject's (orders' or purchasing's, as on their pages).
 *
 * Every command is presented by the server: hidden when it does not apply to
 * this person, disabled with the reason when it is blocked. Acknowledge runs
 * in one click, so its consequence is its dry run's note, read once and shown
 * beside the button rather than as a confirmation step. The subject's
 * commands run in the page's command shell (`onRunSubjectCommand`); opened from
 * the subject's own page, which has them, the sheet points there instead.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AlertCommand,
  NeedsAttentionRecord,
} from "@shared-core/data/needs-attention-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { refusalWords } from "@shared-core/domain/needs-attention";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { Skeleton } from "../../primitives/skeleton";
import { DuePill } from "./AlertDue";
import { MarkAlertDoneDialog } from "./MarkAlertDoneDialog";

const ACKNOWLEDGE = "needs_attention.acknowledge_alert";
const MARK_DONE = "needs_attention.mark_alert_done";

const SUBJECT_WORDS: Record<string, string> = {
  order: "On the order",
  order_line: "On the order line",
  purchase_order: "On the purchase order",
};

export interface AlertSheetProps {
  alertId: string;
  /** Something changed (acknowledged, marked done): re-read the list. */
  onChanged: () => void;
  /** Run one of the subject's commands in the page's command shell; without it, its commands are on its page. */
  onRunSubjectCommand?: (
    command: AlertCommand,
    record: NeedsAttentionRecord,
  ) => void;
}

type Read =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "missing" }
  | { status: "ready"; record: NeedsAttentionRecord };

export function AlertSheet({
  alertId,
  onChanged,
  onRunSubjectCommand,
}: AlertSheetProps) {
  const adapter = useDataAdapter();
  const [read, setRead] = useState<Read>({ status: "loading" });
  const [reloads, setReloads] = useState(0);
  const [isMarkingDone, setIsMarkingDone] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRead((r) => (r.status === "ready" ? r : { status: "loading" }));
    adapter
      .getNeedsAttentionAlert(alertId)
      .then((record) => {
        if (!cancelled)
          setRead(record ? { status: "ready", record } : { status: "missing" });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setRead({
            status: "error",
            message: refusalWords(err) ?? "The alert did not load.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, alertId, reloads]);

  const acknowledgeNote = useAcknowledgeNote(
    read.status === "ready"
      ? read.record.commands.find((c) => c.name === ACKNOWLEDGE)
      : undefined,
  );

  const changed = useCallback(
    (summary: string) => {
      toast.success(summary);
      setReloads((n) => n + 1);
      onChanged();
    },
    [onChanged],
  );

  if (read.status === "loading")
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  if (read.status === "missing")
    return (
      <p className="text-sm text-muted-foreground">
        The alert doesn't exist, or the alert isn't yours to see.
      </p>
    );
  if (read.status === "error")
    return (
      <p role="alert" className="text-sm text-destructive">
        {read.message}
      </p>
    );

  const { record } = read;
  const acknowledge = record.commands.find((c) => c.name === ACKNOWLEDGE);
  const markDone = record.commands.find((c) => c.name === MARK_DONE);

  const runAcknowledge = async (command: AlertCommand) => {
    setIsAcknowledging(true);
    try {
      changed((await adapter.runAlertCommand(command)).summary);
    } catch (err) {
      toast.error(refusalWords(err) ?? "It was not acknowledged. Try again.");
      setReloads((n) => n + 1);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <SubjectLine record={record} />
        <DuePill alert={record} />
      </div>
      {record.cleared_sentence && (
        <p className="rounded-md border border-green-700 px-3 py-2 text-green-700 dark:border-green-400 dark:text-green-400">
          {record.cleared_sentence}.
        </p>
      )}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-xs text-muted-foreground">Customer</dt>
        <dd>{record.customer_name ?? "Stock (no customer)"}</dd>
        <dt className="text-xs text-muted-foreground">Owner</dt>
        <dd>
          {record.owner_name}
          {record.owner_kind === "customer_csr" && (
            <span className="text-muted-foreground"> (customer's CSR)</span>
          )}
        </dd>
        <dt className="text-xs text-muted-foreground">Detected</dt>
        <dd>{formatDateTimeOrDash(record.detected_at)}</dd>
        {record.incident > 1 && (
          <>
            <dt className="text-xs text-muted-foreground">Incident</dt>
            <dd>{record.incident}</dd>
          </>
        )}
        {record.acknowledged_at && (
          <>
            <dt className="text-xs text-muted-foreground">Acknowledged</dt>
            <dd>
              {record.acknowledged_by_name ?? "Someone"},{" "}
              {formatDateTimeOrDash(record.acknowledged_at)}
            </dd>
          </>
        )}
        {record.ticket_url && (
          <>
            <dt className="text-xs text-muted-foreground">Ticket</dt>
            <dd>
              <Link
                className="text-primary underline"
                to={paths.ticketDetail({ id: ticketIdOf(record.ticket_url) })}
              >
                {record.headline}: {record.subject_ref}
              </Link>
              {record.ticket_status && (
                <span className="text-muted-foreground">
                  {" "}
                  · {ticketWords(record.ticket_status)}
                </span>
              )}
            </dd>
          </>
        )}
        {record.done_note && (
          <>
            <dt className="text-xs text-muted-foreground">Note</dt>
            <dd>{record.done_note}</dd>
          </>
        )}
      </dl>

      <section className="border-t border-border pt-3">
        <h3 className="mb-1 font-semibold">Why</h3>
        {record.release_checks.length > 0 ? (
          <>
            <ul aria-label="Release checks" className="space-y-0.5">
              {record.release_checks.map((check) => (
                <li
                  key={check.label}
                  className={
                    check.is_passed
                      ? "text-green-700 dark:text-green-400"
                      : "text-destructive"
                  }
                >
                  <span aria-hidden="true">{check.is_passed ? "✓" : "✗"}</span>{" "}
                  <span className="sr-only">
                    {check.is_passed ? "Passed: " : "Not passed: "}
                  </span>
                  {check.sentence}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-muted-foreground">
              {record.sentence}
            </p>
          </>
        ) : (
          <p>{record.sentence}</p>
        )}
      </section>

      {record.state !== "cleared" && (
        <section className="border-t border-border pt-3">
          <h3 className="mb-2 font-semibold">Alert</h3>
          {record.commands.length === 0 ? (
            <p className="text-muted-foreground">
              No commands: this alert is {record.owner_name}'s.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {acknowledge && (
                <CommandRow
                  command={acknowledge}
                  hint={acknowledgeNote ?? undefined}
                  onRun={() => runAcknowledge(acknowledge)}
                  isBusy={isAcknowledging}
                />
              )}
              {markDone && (
                <CommandRow
                  command={markDone}
                  isPrimary
                  hint="It stays until someone says it is dealt with."
                  onRun={() => setIsMarkingDone(true)}
                />
              )}
              {!markDone && (
                <p className="text-xs text-muted-foreground">
                  It clears on its own when its condition goes.
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {record.subject_commands.length > 0 && !onRunSubjectCommand && (
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          {SUBJECT_WORDS[record.subject_kind] ?? "On its subject"}: its own
          commands are on its page.
        </p>
      )}
      {record.subject_commands.length > 0 && onRunSubjectCommand && (
        <section className="border-t border-border pt-3">
          <h3 className="mb-1 font-semibold">
            {SUBJECT_WORDS[record.subject_kind] ?? "On its subject"}
          </h3>
          <p className="mb-2 text-xs text-muted-foreground">
            Their own commands, as on their page.
          </p>
          <div className="flex flex-col gap-2">
            {record.subject_commands.map((command) => (
              <CommandRow
                key={command.name}
                command={command}
                onRun={() => onRunSubjectCommand?.(command, record)}
              />
            ))}
          </div>
        </section>
      )}

      {markDone && (
        <MarkAlertDoneDialog
          command={markDone}
          open={isMarkingDone}
          onOpenChange={setIsMarkingDone}
          onDone={changed}
        />
      )}
    </div>
  );
}

/**
 * The note Acknowledge's dry run gives (what acknowledging will change), for an
 * enabled Acknowledge only: a blocked one shows its reason instead. A dry run
 * that fails leaves the button without a note; the commit still reports.
 */
function useAcknowledgeNote(command: AlertCommand | undefined): string | null {
  const adapter = useDataAdapter();
  const [note, setNote] = useState<string | null>(null);
  const dryRunHref = command?.enabled ? command.dry_run_href : null;

  useEffect(() => {
    setNote(null);
    if (!command || !dryRunHref) return;
    let cancelled = false;
    adapter
      .dryRunAlertCommand(command, {})
      .then((preview) => {
        if (cancelled) return;
        const lines = preview.display.flatMap((block) =>
          block.kind === "note" ? block.lines : [],
        );
        setNote(lines.length > 0 ? lines.join(" ") : null);
      })
      .catch(() => {
        if (!cancelled) setNote(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the command is re-read with every record; its dry-run href identifies it
  }, [adapter, dryRunHref]);

  return note;
}

function SubjectLine({ record }: { record: NeedsAttentionRecord }) {
  if (record.subject_kind === "purchase_order" && record.purchase_order_no) {
    return (
      <p>
        Purchase order{" "}
        <Link
          className="text-primary underline"
          to={paths.purchaseOrderDetail({
            purchaseOrderNo: record.purchase_order_no,
          })}
        >
          {record.purchase_order_no}
        </Link>
        {record.supplier_name && <span> · {record.supplier_name}</span>}
        {record.order_no && (
          <span className="text-muted-foreground">
            {" "}
            · for {record.order_no}
          </span>
        )}
      </p>
    );
  }
  return (
    <p>
      {record.subject_kind === "order_line" ? "Order line " : "Order "}
      {record.order_no ? (
        <Link
          className="text-primary underline"
          to={paths.orderDetail({ orderNo: record.order_no })}
        >
          {record.subject_ref}
        </Link>
      ) : (
        record.subject_ref
      )}
      {record.subject_description && (
        <span> · {record.subject_description}</span>
      )}
      {record.subject_kind === "order_line" && record.supplier_name && (
        <span> · to {record.supplier_name}</span>
      )}
    </p>
  );
}

function CommandRow({
  command,
  hint,
  isPrimary = false,
  isBusy = false,
  onRun,
}: {
  command: AlertCommand;
  hint?: string;
  isPrimary?: boolean;
  isBusy?: boolean;
  onRun: () => void;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-3">
      <Button
        size="sm"
        variant={isPrimary ? "default" : "outline"}
        disabled={!command.enabled || isBusy}
        onClick={onRun}
      >
        {command.label}
      </Button>
      {!command.enabled && command.reason ? (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {command.reason}
        </span>
      ) : (
        hint && <span className="text-xs text-muted-foreground">{hint}</span>
      )}
    </div>
  );
}

function ticketIdOf(ticketUrl: string): string {
  return decodeURIComponent(ticketUrl.split("/").pop() ?? "");
}

const TICKET_WORDS: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  waiting: "Waiting",
  resolved: "Resolved",
  closed: "Closed",
};

function ticketWords(status: string): string {
  return TICKET_WORDS[status] ?? status;
}
